import type { Seq } from 'immutable';
import Immutable from 'immutable';

import type { FontAsset } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { checkAssetUUID } from 'js/src/data/media';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import md5 from 'md5';

// only load the data once per page – don't duplicate requests
// once a component has loaded the data, simply cache it in this variable
const loaded_fonts = {
    loaded: false,
    loading: false,
    data: Immutable.Map(),
} as {
    loaded: boolean;
    loading: boolean;
    data: Immutable.Map<string, ImmutableMap<FontAsset>>;
};

// Font urls that already have a @font-face rule on the page, so a font the
// template uses outside the library only gets its rule injected once.
const urlsWithInjectedCss = new Set<string>();

class FontLoader {
    /*
    Loads a list of dynamic fonts from the server & sets the font css classes up for the page
    NOTE: the font url->classname determination is done without requiring the font list
          to be fully loaded. Otherwise a lot of extra async handling and data passing
          would be required.
    */
    constructor(didLoadCallback: (fontList: Seq.Indexed<ImmutableMap<FontAsset>>) => void) {
        if (!loaded_fonts.loaded) {
            // fetch font data from server
            this.loadFontsAsync(didLoadCallback);
        } else {
            // use loaded font data
            if (didLoadCallback) {
                didLoadCallback(loaded_fonts.data.valueSeq());
            }
        }
    }

    loadFontsAsync(didLoadCallback: (fontList: Seq.Indexed<ImmutableMap<FontAsset>>) => void) {
        if (loaded_fonts.loaded) {
            // already loaded!
            if (didLoadCallback) {
                didLoadCallback(loaded_fonts.data.valueSeq());
            }
            return;
        }

        if (loaded_fonts.loading) {
            // currently loading –check again and potentially retry in 2 seconds
            window.setTimeout(() => this.loadFontsAsync(didLoadCallback), 2000);
            return;
        }

        loaded_fonts.loading = true;
        ApiService.fontAssetsList()
            .then((response) => {
                const new_fonts_list = ImmutableFromJS(response.data);

                // create map in form <font_uuid>: <font_data>
                let new_fonts_map = Immutable.Map<string, ImmutableMap<FontAsset>>();
                for (let i = 0; i < new_fonts_list.size; i++) {
                    const new_fonts_list_val = new_fonts_list.get(i)!;
                    new_fonts_map = new_fonts_map.set(
                        checkAssetUUID(new_fonts_list_val.get('uuid')),
                        new_fonts_list_val,
                    );
                }

                // Actually build the css and inject it into the page.
                FontLoader.inject_css(new_fonts_list);

                // save values
                loaded_fonts.data = new_fonts_map;
                loaded_fonts.loaded = true;
                if (didLoadCallback) {
                    didLoadCallback(new_fonts_map.valueSeq());
                }
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
            })
            .finally(() => {
                loaded_fonts.loading = false;
            });
    }

    static deleteFont(fontAsset: ImmutableMap<FontAsset>) {
        ApiService.fontAssetsDestroy({
            path: { id: fontAsset.get('id') },
        })
            .then(() => {
                Toast.success('Font successfully deleted');
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Something went wrong deleting the font, please try again later.');
            });
    }

    static addFont(font_asset: ImmutableMap<FontAsset>) {
        loaded_fonts.data = loaded_fonts.data.set(
            checkAssetUUID(font_asset.get('uuid')),
            font_asset,
        );
        FontLoader.inject_css(Immutable.List([font_asset]));
        return loaded_fonts.data;
    }

    static inject_css(fonts_list: Immutable.List<ImmutableMap<FontAsset>>) {
        FontLoader.inject_css_for_urls(fonts_list.map((font) => font.get('url')).toArray());
    }

    static inject_css_for_urls(urls: readonly string[]) {
        const newUrls = urls.filter((url) => !urlsWithInjectedCss.has(url));
        if (newUrls.length === 0) {
            return;
        }
        newUrls.forEach((url) => urlsWithInjectedCss.add(url));
        const css = newUrls.map((url) => FontLoader.generate_font_face_css(url)).join('');
        const style = document.createElement('style');
        style.setAttribute('nonce', window._CSP_NONCE);
        style.appendChild(document.createTextNode(css));

        document.head.appendChild(style);
    }

    static generate_font_face_css(url_src: string): string {
        const className = FontLoader.get_class_for_url(url_src);
        return `
        @font-face {
            font-family: "${className}";
            src: url('${url_src}') format('truetype');
            font-display: swap;
        }
        .${className}, .${className} span, .${className} p, .${className} div {
            font-family: "${className}" !important;
        }
        `;
    }

    static get_class_for_url(url: string): string {
        /*
        The class name is simply the hash of the url – this guarantees a one way mapping
        without requiring any additional information about the font with the stripped name
        to help with debugging (also classes cannot start with numbers)
         */
        if (url === null || url === undefined) {
            return '';
        }
        const filename = url
            .substring(url.lastIndexOf('/') + 1)
            .replace('.tff', '')
            .replace(/[^a-z+]+/gi, '');
        return `${filename}-${md5(url).substr(0, 5)}`;
    }
}

export default FontLoader;
